import { useTranslation } from 'react-i18next';
import { AdblockDailyMetrics } from '../../../types/adblock';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { formatCurrency, formatNumber, formatPercentage } from '../../../utils/formatters';

interface AdblockTableProps {
  dailyMetrics: AdblockDailyMetrics[];
  isLoading: boolean;
}

export function AdblockTable({ dailyMetrics, isLoading }: AdblockTableProps) {
  const { t } = useTranslation();

  // Calculer les métriques supplémentaires
  const enrichedMetrics = dailyMetrics.map(metric => {
    const ctr = metric.impressions > 0 ? (metric.clicks / metric.impressions) * 100 : 0;
    const ecpm = metric.impressions > 0 ? (metric.revenueUSD / metric.impressions) * 1000 : 0;
    const pageRpm = metric.pageviewsBlockingAds > 0 ? (metric.revenueUSD / metric.pageviewsBlockingAds) * 1000 : 0;

    return {
      ...metric,
      ctr,
      ecpm,
      pageRpm
    };
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('adblock.dashboard.table.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-8 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('adblock.dashboard.table.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('adblock.dashboard.table.date')}</TableHead>
              <TableHead className="text-right">{t('adblock.dashboard.table.totalPageviews')}</TableHead>
              <TableHead className="text-right">{t('adblock.dashboard.table.adblockPageviews')}</TableHead>
              <TableHead className="text-right">{t('adblock.dashboard.table.impressions')}</TableHead>
              <TableHead className="text-right">{t('adblock.dashboard.table.clicks')}</TableHead>
              <TableHead className="text-right">{t('adblock.dashboard.table.ctr')}</TableHead>
              <TableHead className="text-right">{t('adblock.dashboard.table.ecpm')}</TableHead>
              <TableHead className="text-right">{t('adblock.dashboard.table.grossRevenue')}</TableHead>
              <TableHead className="text-right">{t('adblock.dashboard.table.publisherRevenue')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {enrichedMetrics.map((metric, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">
                  {new Date(metric.date).toLocaleDateString('fr-FR', { 
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </TableCell>
                <TableCell className="text-right">{formatNumber(metric.pageviews)}</TableCell>
                <TableCell className="text-right">{formatNumber(metric.pageviewsBlockingAds)}</TableCell>
                <TableCell className="text-right">{formatNumber(metric.impressions)}</TableCell>
                <TableCell className="text-right">{formatNumber(metric.clicks)}</TableCell>
                <TableCell className="text-right">{formatPercentage(metric.ctr)}</TableCell>
                <TableCell className="text-right">{formatCurrency(metric.ecpm)}</TableCell>
                <TableCell className="text-right">{formatCurrency(metric.revenueUSD)}</TableCell>
                <TableCell className="text-right">{formatCurrency(metric.publisherRevenueNet)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}